import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Card } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useDebounced } from '../../lib/useDebounced';
import type { ArticleSummary } from './data';

// La Base de Conocimientos dentro de los tickets (guía 7.8): sugerencias al escribir el problema, el selector con el
// que IT inserta un artículo en su respuesta y los códigos KB-000123 del hilo convertidos en enlaces.

/** Artículos parecidos a un texto (desde 3 letras, mientras se escribe). */
export function useArticleSuggestions(text: string) {
  const q = useDebounced(text.trim(), 400);
  return useQuery({
    queryKey: ['conocimiento', 'sugerencias', q],
    queryFn: () => api<ArticleSummary[]>(`/api/conocimiento/sugerencias?${new URLSearchParams({ texto: q })}`),
    enabled: q.length >= 3,
    staleTime: 60_000,
  });
}

/** Al abrir un ticket: guías que quizá resuelven el problema sin esperar a IT. Se abren en otra pestaña. */
export function SuggestedArticles({ text }: { text: string }) {
  const suggestions = useArticleSuggestions(text);
  if (!suggestions.data?.length || text.trim().length < 3) return null;
  return (
    <section aria-live="polite" aria-label={t('kb.sugeridos')} className="rounded-md border border-marca/30 bg-marca-suave px-4 py-3">
      <h2 className="text-sm font-medium text-slate-900">{t('kb.sugeridos')}</h2>
      <ul className="mt-2 space-y-1.5 text-sm">
        {suggestions.data.map((a) => (
          <li key={a.id}>
            <a href={`/conocimiento/${a.code}`} target="_blank" rel="noopener" className="font-medium text-marca hover:underline">
              {a.title}
            </a>
            {a.summary && <span className="block text-xs text-slate-600">{a.summary}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** IT busca un artículo y lo inserta en su respuesta. */
export function ArticlePicker({ initial, onPick, onClose }: { initial: string; onPick: (a: ArticleSummary) => void; onClose: () => void }) {
  const [text, setText] = useState(initial);
  const suggestions = useArticleSuggestions(text);
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2">
        <label htmlFor="kb-insertar" className="sr-only">
          {t('kb.insertar_buscar')}
        </label>
        <input
          id="kb-insertar"
          type="search"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          placeholder={t('kb.insertar_buscar')}
          className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/25"
        />
        <button type="button" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900">
          {t('comun.cancelar')}
        </button>
      </div>
      {text.trim().length >= 3 && suggestions.data && (
        suggestions.data.length === 0 ? (
          <p className="text-xs text-slate-500">{t('kb.insertar_vacio')}</p>
        ) : (
          <ul className="space-y-1" aria-label={t('kb.resultados')}>
            {suggestions.data.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => onPick(a)} className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-white focus:bg-white focus:outline-none">
                  <span className="font-mono text-xs text-slate-500">{a.code}</span> <span className="text-slate-900">{a.title}</span>
                  {a.audience === 'it' && (
                    <span className="ml-2">
                      <Badge tone="amber">{t('kb.solo_it')}</Badge>
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

const CODE = /(\bKB-\d{6}\b)/;

/** Texto de un mensaje con los códigos de artículo como enlaces. */
export function TextWithArticleLinks({ text }: { text: string }) {
  const parts = text.split(CODE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Link key={i} to={`/conocimiento/${part}`} className="font-medium text-marca hover:underline">
            {part}
          </Link>
        ) : (
          part
        ),
      )}
    </>
  );
}

/** Artículos usados en un ticket. */
export function UsedArticlesCard({ articles }: { articles: Array<{ id: string; code: string; title: string; audience: string }> }) {
  if (articles.length === 0) return null;
  return (
    <Card variant="aside" title={t('kb.usados')}>
      <ul className="space-y-1.5 text-sm">
        {articles.map((a) => (
          <li key={a.id}>
            <Link to={`/conocimiento/${a.code}`} className="text-marca hover:underline">
              {a.title}
            </Link>{' '}
            <span className="font-mono text-xs text-slate-500">{a.code}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
